// The header's Quick Add menu (DLG-008, WF-008; legacy layouts/app/_partials/AppHeaderUserMenu.vue): a "+" button whose
// menu lists Add Item, Add Category, Add Modifier Set, Add Customer, Add Employee and Add Discount, each only where the
// role may open that page. A pick goes to the page with `?add=true`, and the page opens its own create dialog
// (`useQuickAdd`), also when it is the page already open (legacy opened the dialog in place then). Each action shows
// legacy's own illustration (public/assets/quick-actions, its dark-mode one in dark mode) in a 40 px avatar over a 12 px
// label, three across in a 400 px q-menu hung from the button's right edge.
import { useEffect, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { DropdownMenu } from 'radix-ui';
import { CirclePlus } from 'lucide-react';
import { useActiveLocation } from '../app/location.tsx';
import { mayOpen } from '../app/page-permissions.ts';
import { pathFor, type AppPath } from '../router-paths.ts';

interface QuickAction {
  readonly name: string;
  readonly path: AppPath;
  /** Legacy's illustration (constants.ts ICON_PATHS.QUICK_ACTION), light and dark. */
  readonly icon: string;
}

/** Legacy's six quick actions, in its order. */
const QUICK_ACTIONS: readonly QuickAction[] = [
  { name: 'Add Item', path: '/l/$slug/menu/items/{-$itemId}', icon: 'add_item' },
  { name: 'Add Category', path: '/l/$slug/menu/categories', icon: 'add_category' },
  { name: 'Add Modifier Set', path: '/l/$slug/menu/modifiers', icon: 'add_mod_set' },
  { name: 'Add Customer', path: '/l/$slug/customers/list', icon: 'add_customer' },
  { name: 'Add Employee', path: '/l/$slug/employees/list', icon: 'add_employee' },
  { name: 'Add Discount', path: '/l/$slug/discounts/preset', icon: 'add_discount' },
];

/** Opens the page's create dialog when the URL asks (`?add=true`), then takes the ask off the URL. */
export function useQuickAdd(open: () => void): void {
  const search = useSearch({ strict: false }) as { add?: unknown };
  const navigate = useNavigate();
  const asked = search.add === true || search.add === 'true';
  const opener = useRef(open);
  opener.current = open;
  useEffect(() => {
    if (!asked) return;
    opener.current();
    const rest = (prev: Record<string, unknown>) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== 'add'));
    void navigate({ to: '.', search: rest as never, replace: true });
  }, [asked, navigate]);
}

/** The header's "+" button and its menu; nothing when the role may open none of the pages. */
export function QuickAddMenu() {
  const location = useActiveLocation();
  const navigate = useNavigate();
  const actions = QUICK_ACTIONS.filter((a) => mayOpen(a.path, location.can));
  if (actions.length === 0) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger aria-label="Quick Add" title="Quick Add" data-testid="quick-add"
        className="inline-flex size-9 items-center justify-center rounded-q border border-line bg-surface text-text-2 data-[state=open]:border-primary data-[state=open]:text-primary">
        <CirclePlus size={20} strokeWidth={1.5} aria-hidden />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={0} data-testid="quick-add-menu"
          className="z-[3000] grid w-[400px] max-w-[calc(100vw-32px)] grid-cols-3 gap-1 rounded-q border border-line bg-menu p-2 shadow-lg dark:border-transparent dark:shadow-none">
          {actions.map(({ name, path, icon }) => (
            // Legacy: a flat no-caps q-btn (4 px top and bottom) holding a 40 px rounded q-avatar and the name 8 px under it.
            <DropdownMenu.Item key={name} data-testid={`quick-add.${icon}`}
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-q px-2 py-1 text-[12px] leading-5 text-menu-ink outline-none data-[highlighted]:bg-menu-item-hover"
              onSelect={() => void navigate({ to: pathFor(path, location.slug), search: { add: true } as never })}>
              <img src={`/assets/quick-actions/light/${icon}.svg`} alt="" width={40} height={40} className="size-10 rounded-q dark:hidden" />
              <img src={`/assets/quick-actions/dark/${icon}_dark.svg`} alt="" width={40} height={40} className="hidden size-10 rounded-q dark:block" />
              {name}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
